import { FC, lazy, useCallback, useMemo } from 'react';

import {
  ActFormHeader,
  ActStepperConfig,
  ActStepperEngine,
  ActStepperSubmitMethod,
} from '@gasbi/shared/web/components/ActStepperEngine';
import { useNavigate } from 'react-router-dom';

import { ActFormTitle } from '@/pages/ViolationActForm/components/ActFormTitle';
import { useSubmitForm } from '@/pages/ViolationActForm/hooks';

import { useActFormBasePath } from '@components/ActsAndDocumentsForm';

export enum ViolationActFormStep {
  Information = 'Information',
  Devices = 'Devices',
}

export const ActFormView: Record<keyof typeof ViolationActFormStep, string> = {
  Information: '',
  Devices: 'devices',
};

export type ViolationActFormProps = {
  currentView?: 'meteringPoint' | 'commercialMeteringPoint';
  isEditMode?: boolean;
};

const InformationStep = lazy(
  () => import('@/pages/ViolationActForm/views/Information'),
);
const DevicesStep = lazy(
  () => import('@/pages/ViolationActForm/views/Devices'),
);

const STEPS = [
  {
    key: ViolationActFormStep.Information,
    label: 'Загальна інформація',
    component: InformationStep,
  },
  {
    key: ViolationActFormStep.Devices,
    label: 'Прилади обліку',
    component: DevicesStep,
  },
];

const ViolationActForm: FC<ViolationActFormProps> = ({
  currentView = 'commercialMeteringPoint',
  isEditMode,
}) => {
  const navigate = useNavigate();
  const basePath = useActFormBasePath(currentView);
  const onClose = useCallback(() => navigate(basePath), [navigate, basePath]);

  const { submit: submitForm } = useSubmitForm({
    isEditMode,
    currentSteps: ViolationActFormStep,
  });

  const submitMethod: ActStepperSubmitMethod = useCallback(
    ({ stepsState, selectedFiles, documentsToDelete }) =>
      submitForm({ stepsState, selectedFiles, documentsToDelete }),
    [submitForm],
  );

  const config: ActStepperConfig = useMemo(
    () => ({
      steps: STEPS,
      isEditMode,
      currentView,
      storageKey: 'VIOLATION_ACT_FORM_DATA',
      submitMethod,
      header: <ActFormHeader steps={STEPS} />,
      renderTitle: outletRef => <ActFormTitle buttonRef={outletRef} />,
      onClose,
    }),
    [isEditMode, currentView, submitMethod, onClose],
  );

  return <ActStepperEngine config={config} />;
};

export default ViolationActForm;
